/**
 * Specification Detail Component
 * @implements FS-004.5.2 - View Specification Details
 * @implements US-029 - Basic Specification Viewing
 *
 * Displays full specification content including:
 * - Overview
 * - Functional requirements with FS-XXX.X identifiers
 * - Business rules with BS-XXX identifiers
 * - Data requirements, user interactions, edge cases, and dependencies
 */

import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  Chat as ChatIcon,
  CheckCircle as CheckCircleIcon,
  CompareArrows as CompareArrowsIcon,
  Description as DescriptionIcon,
  EditNote as EditNoteIcon,
  Rule as RuleIcon,
} from '@mui/icons-material';
import { useQuery, gql } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { SpecificationStatus } from './SpecificationList';
import { SpecificationDiffView } from './SpecificationDiffView';
import ReactMarkdown from 'react-markdown';

/**
 * GraphQL query for fetching a single specification
 * @implements FS-004.5.2 - View Specification Details
 */
const GET_SPECIFICATION_QUERY = gql`
  query GetSpecification($id: ID!) {
    specification(id: $id) {
      id
      projectId
      specificationNumber
      formattedNumber
      title
      status
      content {
        overview
        functionalRequirements {
          id
          title
          description
          acceptanceCriteria
        }
        businessRuleIds
        dataRequirements
        userInteractions
        edgeCases
        dependencies
      }
      previousContent {
        overview
        functionalRequirements {
          id
          title
          description
          acceptanceCriteria
        }
        businessRuleIds
        dataRequirements
        userInteractions
        edgeCases
        dependencies
      }
      businessRules {
        id
        specificationId
        ruleNumber
        formattedNumber
        fullIdentifier
        statement
        rationale
        examples
        createdAt
        updatedAt
      }
      originatingConversationId
      createdAt
      updatedAt
    }
  }
`;

/**
 * Functional requirement type
 * @implements FS-004.2.1 - Specification Structure
 */
export interface FunctionalRequirement {
  id: string;
  title: string;
  description: string;
  acceptanceCriteria: string[];
}

/**
 * Business rule type
 * @implements FS-004.2.1 - Business Rule Structure
 */
export interface BusinessRule {
  id: string;
  specificationId: string;
  ruleNumber: number;
  formattedNumber: string;
  fullIdentifier: string;
  statement: string;
  rationale: string;
  examples: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Specification content type
 * @implements FS-004.2.1 - Specification Structure
 */
export interface SpecificationContent {
  overview: string;
  functionalRequirements: FunctionalRequirement[];
  businessRuleIds: string[];
  dataRequirements: string;
  userInteractions: string;
  edgeCases: string;
  dependencies: string;
}

/**
 * Full specification type
 * @implements US-059 - previousContent for diff detection
 */
export interface Specification {
  id: string;
  projectId: string;
  specificationNumber: number;
  formattedNumber: string;
  title: string;
  status: SpecificationStatus;
  content: SpecificationContent;
  /** Previous content before the last edit. Null if never edited. */
  previousContent: SpecificationContent | null;
  businessRules: BusinessRule[];
  originatingConversationId: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Response type for GetSpecification query
 */
interface GetSpecificationResponse {
  specification: Specification | null;
}

/**
 * Variables for GetSpecification query
 */
interface GetSpecificationVariables {
  id: string;
}

/**
 * Props for SpecificationDetail component
 */
export interface SpecificationDetailProps {
  /** Specification ID to fetch */
  specificationId: string;
  /** Project ID for navigation context */
  projectId: string;
  /** Whether to show the "Discuss with PM" button */
  showDiscussButton?: boolean;
  /** Callback when "Discuss with PM" is clicked */
  onDiscussClick?: () => void;
}

/**
 * Status badge component for specifications
 * @implements FS-004.5.1 - Draft specifications visually distinguished
 */
const StatusBadge = ({
  status,
  size = 'medium',
}: {
  status: SpecificationStatus;
  size?: 'small' | 'medium';
}) => {
  const isDraft = status === 'DRAFT';

  return (
    <Chip
      label={status}
      size={size}
      color={isDraft ? 'warning' : 'success'}
      variant={isDraft ? 'outlined' : 'filled'}
      icon={isDraft ? <EditNoteIcon /> : <CheckCircleIcon />}
      sx={{ fontWeight: 500 }}
    />
  );
};

/**
 * Section component for displaying content sections
 */
const Section = ({
  title,
  children,
  icon,
}: {
  title: string;
  children: React.ReactNode;
  icon?: React.ReactNode;
}) => (
  <Box sx={{ mb: 4 }}>
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
      {icon}
      <Typography variant="h6" component="h3">
        {title}
      </Typography>
    </Stack>
    {children}
  </Box>
);

/**
 * Markdown content renderer
 * @implements US-029 - Markdown formatting rendered properly
 */
const MarkdownContent = ({ content }: { content: string }) => {
  if (!content || content.trim() === '') {
    return (
      <Typography variant="body2" color="text.secondary" fontStyle="italic">
        No content specified.
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        '& p': { mt: 0, mb: 1.5 },
        '& ul, & ol': { mt: 0, mb: 1.5, pl: 3 },
        '& li': { mb: 0.5 },
        '& code': {
          bgcolor: 'grey.100',
          px: 0.5,
          py: 0.25,
          borderRadius: 0.5,
          fontFamily: 'monospace',
          fontSize: '0.875em',
        },
        '& pre': {
          bgcolor: 'grey.100',
          p: 2,
          borderRadius: 1,
          overflow: 'auto',
        },
        '& pre code': {
          bgcolor: 'transparent',
          p: 0,
        },
      }}
    >
      <ReactMarkdown>{content}</ReactMarkdown>
    </Box>
  );
};

/**
 * SpecificationDetail - Displays full specification content
 *
 * @implements FS-004.5.2 - View Specification Details
 * @implements US-029 - Basic Specification Viewing
 *
 * Features:
 * - Displays all specification sections
 * - Shows functional requirements with FS-XXX.X identifiers
 * - Shows business rules with BS-XXX identifiers
 * - Renders markdown formatting
 * - "Discuss with PM" action for PM integration
 */
export const SpecificationDetail = ({
  specificationId,
  projectId,
  showDiscussButton = true,
  onDiscussClick,
}: SpecificationDetailProps) => {
  const navigate = useNavigate();

  /**
   * Whether to show the diff view.
   * null = not yet decided (show banner if previousContent exists)
   * true = user clicked to view the diff
   * false = user dismissed the diff
   * @implements US-059 - Diff view toggle state
   */
  const [showDiff, setShowDiff] = useState<boolean | null>(null);

  // Fetch specification
  const { data, loading, error } = useQuery<
    GetSpecificationResponse,
    GetSpecificationVariables
  >(GET_SPECIFICATION_QUERY, {
    variables: { id: specificationId },
    fetchPolicy: 'cache-and-network',
  });

  const specification = data?.specification;

  // Handle "Discuss with PM" click
  // @implements US-029 - PM Integration
  const handleDiscussClick = () => {
    if (onDiscussClick) {
      onDiscussClick();
    } else {
      // Default: navigate to project view (which has the conversation)
      navigate(`/projects/${projectId}`);
    }
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Render loading state
  if (loading && !data) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 8,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Render error state
  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Failed to load specification: {error.message}
      </Alert>
    );
  }

  // Render not found state
  if (!specification) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 4,
          textAlign: 'center',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <DescriptionIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          Specification Not Found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          The specification you are looking for does not exist or you do not
          have access to it.
        </Typography>
      </Paper>
    );
  }

  const { content, businessRules } = specification;

  /**
   * Whether this specification has been edited (previousContent exists).
   * @implements US-059 - Diff detection via previousContent presence
   */
  const hasBeenEdited = specification.previousContent !== null;

  return (
    <>
      {/* Diff Banner - shows when spec has been edited and diff not yet viewed/dismissed */}
      {/* @implements US-059 - "Recent changes available - View diff" banner */}
      {hasBeenEdited && showDiff === null && (
        <Alert
          severity="info"
          icon={<CompareArrowsIcon />}
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => setShowDiff(true)}
            >
              View Diff
            </Button>
          }
        >
          This specification was recently edited. View the changes to see what
          was modified.
        </Alert>
      )}

      {/* Diff View - shows when user clicks "View diff" */}
      {/* @implements US-059 - Structured diff rendering */}
      {hasBeenEdited && showDiff === true && (
        <SpecificationDiffView
          specificationId={specificationId}
          onDismiss={() => setShowDiff(false)}
        />
      )}

      <Paper
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 3,
            bgcolor: 'grey.50',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            sx={{ mb: 2 }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              {/* Specification Number */}
              <Typography
                variant="h5"
                component="span"
                sx={{
                  fontFamily: 'monospace',
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 1,
                  fontWeight: 600,
                }}
              >
                {specification.formattedNumber}
              </Typography>
              {/* Title */}
              <Typography variant="h5" component="h2">
                {specification.title}
              </Typography>
              {/* Status Badge */}
              <StatusBadge status={specification.status} />
            </Stack>

            {/* Discuss with PM button */}
            {/* @implements US-029 - PM Integration */}
            {showDiscussButton && (
              <Button
                variant="outlined"
                startIcon={<ChatIcon />}
                onClick={handleDiscussClick}
              >
                Discuss with PM
              </Button>
            )}
          </Stack>

          <Typography variant="body2" color="text.secondary">
            Last updated {formatDate(specification.updatedAt)}
          </Typography>
        </Box>

        {/* Content */}
        <Box sx={{ p: 3 }}>
          {/* Overview */}
          <Section title="Overview" icon={<DescriptionIcon color="primary" />}>
            <MarkdownContent content={content.overview} />
          </Section>

          <Divider sx={{ my: 3 }} />

          {/* Functional Requirements */}
          {/* @implements US-029 - Functional requirements displayed with FS-XXX.X identifiers */}
          <Section
            title="Functional Requirements"
            icon={<CheckCircleIcon color="primary" />}
          >
            {content.functionalRequirements.length === 0 ? (
              <Typography
                variant="body2"
                color="text.secondary"
                fontStyle="italic"
              >
                No functional requirements defined.
              </Typography>
            ) : (
              <Stack spacing={3}>
                {content.functionalRequirements.map((req) => (
                  <Paper
                    key={req.id}
                    elevation={0}
                    sx={{
                      p: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderLeft: '4px solid',
                      borderLeftColor: 'primary.main',
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ mb: 1 }}
                    >
                      <Chip
                        label={req.id}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ fontFamily: 'monospace', fontWeight: 600 }}
                      />
                      <Typography variant="subtitle1" fontWeight={500}>
                        {req.title}
                      </Typography>
                    </Stack>
                    <MarkdownContent content={req.description} />
                    {req.acceptanceCriteria.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography
                          variant="subtitle2"
                          color="text.secondary"
                          sx={{ mb: 1 }}
                        >
                          Acceptance Criteria:
                        </Typography>
                        <Box component="ul" sx={{ m: 0, pl: 3 }}>
                          {req.acceptanceCriteria.map((criteria, index) => (
                            <Typography
                              component="li"
                              variant="body2"
                              key={index}
                              sx={{ mb: 0.5 }}
                            >
                              {criteria}
                            </Typography>
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Paper>
                ))}
              </Stack>
            )}
          </Section>

          <Divider sx={{ my: 3 }} />

          {/* Business Rules */}
          {/* @implements US-029 - Business rules displayed with BS-XXX identifiers */}
          <Section title="Business Rules" icon={<RuleIcon color="primary" />}>
            {businessRules.length === 0 ? (
              <Typography
                variant="body2"
                color="text.secondary"
                fontStyle="italic"
              >
                No business rules defined.
              </Typography>
            ) : (
              <Stack spacing={3}>
                {businessRules.map((rule) => (
                  <Paper
                    key={rule.id}
                    elevation={0}
                    sx={{
                      p: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderLeft: '4px solid',
                      borderLeftColor: 'warning.main',
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ mb: 1 }}
                    >
                      <Chip
                        label={rule.fullIdentifier}
                        size="small"
                        color="warning"
                        variant="outlined"
                        sx={{ fontFamily: 'monospace', fontWeight: 600 }}
                      />
                    </Stack>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      <strong>Rule:</strong> {rule.statement}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Rationale:</strong> {rule.rationale}
                    </Typography>
                    {rule.examples && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Examples:</strong>
                        </Typography>
                        <MarkdownContent content={rule.examples} />
                      </Box>
                    )}
                  </Paper>
                ))}
              </Stack>
            )}
          </Section>

          <Divider sx={{ my: 3 }} />

          {/* Data Requirements */}
          <Section title="Data Requirements">
            <MarkdownContent content={content.dataRequirements} />
          </Section>

          <Divider sx={{ my: 3 }} />

          {/* User Interactions */}
          <Section title="User Interactions">
            <MarkdownContent content={content.userInteractions} />
          </Section>

          <Divider sx={{ my: 3 }} />

          {/* Edge Cases */}
          <Section title="Edge Cases">
            <MarkdownContent content={content.edgeCases} />
          </Section>

          <Divider sx={{ my: 3 }} />

          {/* Dependencies */}
          <Section title="Dependencies">
            <MarkdownContent content={content.dependencies} />
          </Section>
        </Box>
      </Paper>
    </>
  );
};
